import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import format from 'string-template';
import moment from 'moment';

import ExternalCoachContext from '../../../CoachTools/context/ExternalCoachContext';
import CoachToolsChatContext from '../../../CoachTools/context/ChatContext';
import ChatContext from '../../context/ChatContext';
import ChatChannelContext, {
  withChatChannelContextProviderReady,
} from '../../context/ChatChannelContext';
import useToast from '../../hooks/useToast';
import useComponentMounted from '../../hooks/useComponentMounted';
import useTimeForActionLogger, { ActionEvent } from '../../../hooks/useTimeForActionLogger';
import MessageInputContext from '../../context/MessageInputContext';
import { isChatEnabled } from '../../utils/featureToggles';
import Error from '../Error';
import EmptyView from '../EmptyView';
import ChannelHeader from '../ChannelHeader';
import ChannelMessageList from '../ChannelMessageList';
import ChannelMessageInput from '../ChannelMessageInput';
import MessageSchedulerModal, { SCHEDULER_MODAL_TAB } from '../MessageSchedulerModal';
import ConfirmDialog from '../ConfirmDialog';
import LoadingOverlay from '../LoadingOverlay';
import User from '../../../Model/User';
import { ChannelClaimedFrom } from '../../Model/ChatChannel';
import { Event } from '../../utils/events';
import { ReplyBlockReason, getReplyBlockReason } from '../../utils/replyBlock';
import { VoiceNotePilotGroup, getVoiceNotePilotBannerGroup } from '../../utils/voiceNotePilot';
import SmartContext from '../SmartContext';
import ThreadInbox from '../ThreadInbox';
import {
  Container,
  DrawerSlot,
  NotificationBox,
  LinkSpan,
  TextContainer,
  ClientInactiveNotification,
  VoiceNotePilotBanner,
  VoiceNotePilotMicIcon,
  SingleChannelWrapper,
} from './styles';
import texts from './texts.json';
import { chatChannelError } from './utils';

const REPLY_BLOCK_BANNER_TEXTS = {
  [ReplyBlockReason.CANCELED]: texts.cancelledClientMessage,
  [ReplyBlockReason.UNPAID]: texts.unpaidClientMessage,
  [ReplyBlockReason.PAUSED]: texts.pausedClientMessage,
};

const SingleChannelView = ({
  title,
  width,
  isWebView,
  isMobileView,
}) => {
  const {
    activeChannel,
    isUploading,
    uploadProgress,
    unreadCountDoc,
    scheduledCount,
    pendingScheduledCount,
    channelError,
    isReady,
    smartContextText,
    setSmartContextText,
    setQuotedMessage,
    isThreadInboxOpen,
  } = useContext(ChatChannelContext);

  const {
    logEvent,
    authUser,
    sessionStore,
    smartContextOpen,
    isSmartContextEnabled,
    isVoiceNotePilotCoach,
  } = useContext(ChatContext);
  const { coachDoc, isReady: isExternalCoachReady } = useContext(ExternalCoachContext);
  const {
    pendingOutreachType,
    setPendingOutreachType,
    pendingReply,
    setPendingReply,
  } = useContext(CoachToolsChatContext);

  const {
    isCoachAssistant, isCoachOrAdmin, isCoach, isCoachAndCoachAssistant,
  } = sessionStore;
  const isSchedulerEnabled = isCoachAssistant || isCoachOrAdmin;

  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const [isSchedulerModalOpen, setIsSchedulerModalOpen] = useState(false);
  const [schedulerModalTab, setSchedulerModalTab] = useState(SCHEDULER_MODAL_TAB.NEW);
  const [isChannelReady, setIsChannelReady] = useState(false);
  const [claimee, setClaimee] = useState(null);
  const [channelUserDoc, setChannelUserDoc] = useState(null);
  const [smartContextIsManual, setSmartContextIsManual] = useState(false);
  const [additionalQuery, setAdditionalQuery] = useState('');
  const [smartContextTraceId, setSmartContextTraceId] = useState('');
  const [isChatFeatureEnabled, setIsChatFeatureEnabled] = useState(true);

  const { messageInputRef } = useContext(MessageInputContext);

  const messageListEndRef = useRef(null);
  const hasRunOnMount = useRef(false);
  const runCleanup = useRef(false);
  const unreadCountDocRef = useRef(unreadCountDoc);
  const hasFiredOpenEventRef = useRef(false);
  const activeChannelId = activeChannel?.id;
  const { markStart: markChatStart, logSuccess: logMsgSendTime } = useTimeForActionLogger({
    actionEvent: ActionEvent.CHAT,
    autoStart: false,
  });

  const totalProgress = Object.values(uploadProgress)
    .reduce((acc, val) => acc + val, 0) / Object.keys(uploadProgress).length;

  // This effect is used to trigger the cleanup function when the component actually dismounts
  useEffect(() => () => {
    runCleanup.current = true;
  }, []);

  // Keep unreadCountDocRef current so the cleanup closure can read the latest value
  useEffect(() => {
    unreadCountDocRef.current = unreadCountDoc;
  }, [unreadCountDoc]);

  // Reset the open-event flag when the active channel changes
  useEffect(() => {
    hasFiredOpenEventRef.current = false;
  }, [activeChannelId]);

  // Claim/Unclaim channel on component mount/dismount
  useEffect(() => {
    const claimChannel = async () => {
      if (!!activeChannel && !hasRunOnMount.current) {
        hasRunOnMount.current = true;
        if (isCoachAssistant) {
          const claimData = await activeChannel.claim(authUser.id, authUser.name, ChannelClaimedFrom.CHAT);
          // Set the claimee information if the channel is already claimed, so that the user can reclaim it
          if (isComponentMountedRef.current && !!claimData && claimData.claimedBy !== authUser.id) {
            setClaimee(claimData);
          }
        }
        if (isComponentMountedRef.current) {
          setIsChannelReady(true);
        }
      }
    };

    claimChannel();

    return () => {
      if (activeChannel && unreadCountDocRef.current?.unreadCount > 0) {
        logEvent(Event.CHAT_CLOSED_UNREAD, {
          thread_id: activeChannel.id,
          unread_count: unreadCountDocRef.current.unreadCount,
        });
      }

      if (
        !!activeChannel
        && isCoachAssistant
        && runCleanup.current
      ) {
        activeChannel.unclaim(authUser.id);
      }

      // clear the smart context text when the channel is unmounted
      setSmartContextText('');
    };
  }, [
    isComponentMountedRef,
    authUser,
    activeChannel,
    isCoachAssistant,
    setSmartContextText,
    logEvent,
  ]);

  useEffect(() => {
    if (!isUploading && isReady && isChannelReady && !hasFiredOpenEventRef.current) {
      hasFiredOpenEventRef.current = true;
      markChatStart();
      logEvent(Event.CHAT_THREAD_OPENED, {
        thread_id: activeChannelId,
        unread_count: unreadCountDoc?.unreadCount || 0,
        user_role: isCoachAssistant ? 'AC' : 'coach',
      });
      messageInputRef.current?.focus();
    }
  }, [
    isUploading,
    isReady,
    isChannelReady,
    markChatStart,
    messageInputRef,
    logEvent,
    activeChannelId,
    unreadCountDoc,
    isCoachAssistant,
  ]);

  const overrideText = useMemo(() => {
    if (smartContextText) {
      return {
        source: 'smartContext',
        value: smartContextText,
        isManual: smartContextIsManual,
        traceId: smartContextTraceId,
      };
    }

    return undefined;
  }, [
    smartContextText,
    smartContextIsManual,
    smartContextTraceId,
  ]);

  /**
   * Effect to set the client user doc for the channel.
   * Only the coach or admin need the client user doc for the channel.
   */
  useEffect(() => {
    const setClientUserDoc = async () => {
      if (activeChannel?.userId) {
        const clientUserDoc = await User.getById(activeChannel.userId, {
          viewAsCoachId: activeChannel.coachId,
        });
        setChannelUserDoc(clientUserDoc);
      }
    };
    if (isCoachOrAdmin) {
      setClientUserDoc();
    }
  }, [activeChannel, isCoachOrAdmin]);

  useEffect(() => {
    const checkChatEnabled = async () => {
      const isChatEnabledForUser = await isChatEnabled(activeChannel?.userId, activeChannel?.coachId);
      setIsChatFeatureEnabled(isChatEnabledForUser);
    };
    checkChatEnabled();
  }, [activeChannel]);

  // Arm the quoted-reply state once the intended channel is open and ready
  useEffect(() => {
    if (!pendingReply || !isReady || !activeChannel) return;
    if (activeChannel.userId !== pendingReply.userId) return;
    setQuotedMessage(pendingReply.quotedMessage);
    setPendingReply(null);
  }, [pendingReply, isReady, activeChannel, setQuotedMessage, setPendingReply]);

  const onSchedulerModalOpen = () => {
    setSchedulerModalTab(SCHEDULER_MODAL_TAB.NEW);
    setIsSchedulerModalOpen(true);
  };

  const onSchedulerModalDismiss = () => {
    setIsSchedulerModalOpen(false);
  };

  const showSchedulingSuccessToast = () => {
    showToast(texts.messageScheduled);
  };

  const onClickViewMessagesLink = (type) => {
    setSchedulerModalTab(type);
    setIsSchedulerModalOpen(true);
  };

  const onMessageSent = async (textSent) => {
    if (messageListEndRef.current) {
      messageListEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    if (smartContextText) {
      logEvent(Event.SMART_CONTEXT_MESSAGE_SENT, {
        userId: authUser?.id,
        timestamp: new Date().toISOString(),
        edited: textSent !== smartContextText,
        channelId: activeChannelId,
        isManual: smartContextIsManual,
      });
    }

    // Log the time taken to send the message
    logMsgSendTime({
      chatChannelId: activeChannelId,
      isGeneratedByAI: !!smartContextText,
      clientId: activeChannel?.userId,
    });

    if (pendingOutreachType) {
      setPendingOutreachType(null);
    }

    if (pendingReply) {
      setPendingReply(null);
    }

    setSmartContextText('');
    setSmartContextIsManual(false);
    setSmartContextTraceId('');
  };

  const reclaimChannel = useCallback(() => {
    setClaimee(null);
    if (activeChannel) {
      activeChannel.reclaim(authUser.id, authUser.name, ChannelClaimedFrom.CHAT);
    }
  }, [
    activeChannel,
    authUser,
  ]);

  /**
   * The chat input is hidden if,
   * 1. The client's subscription blocks replies - see getReplyBlockReason, which the Unflag message
   *    action shares so the two can never disagree on whether a reply is possible.
   * 2. The chat feature is not enabled for the user.
   * Additionally, until we fetch channel user doc and the coach doc, we don't show the chat input.
   */
  const isChannelUserDocReady = (activeChannel?.userId === channelUserDoc?.id) || !isCoachOrAdmin;
  const readyChannelUserDoc = isChannelUserDocReady ? channelUserDoc : null;
  const replyBlockReason = getReplyBlockReason({
    isCoachOrAdmin,
    clientUserDoc: readyChannelUserDoc,
    coachDoc,
  });

  const shouldShowChatInput = isChatFeatureEnabled && (
    !isCoachOrAdmin
    || (isChannelUserDocReady
      && isExternalCoachReady
      && !replyBlockReason)
  );
  const shouldShowHiddenBanner = isChannelUserDocReady && !!replyBlockReason;
  const voiceNotePilotGroup = getVoiceNotePilotBannerGroup({
    isCoachOrAdmin,
    isPilotCoach: isVoiceNotePilotCoach,
    canMessageClient: shouldShowChatInput,
    clientId: readyChannelUserDoc?.id,
    firstServiceStartAt: readyChannelUserDoc?.firstServiceStartAt,
  });
  const isVoiceNotePilotVariant = voiceNotePilotGroup === VoiceNotePilotGroup.VARIANT;

  /*
    One side drawer at a time. Both occupants want the same grid column, and letting them share it
    would squeeze the conversation to about half a laptop screen — so the thread inbox takes the
    slot while it is open and Smart Context comes back when it closes.

    Comes back, not restarts: Smart Context stays mounted and hidden rather than unmounting, because
    it holds its answer, its query and an in-flight generation in local state. Opening the inbox
    mid-generation must not throw that away.
  */
  const isSmartContextActive = smartContextOpen && isWebView
  && (!isCoach || isCoachAndCoachAssistant || isSmartContextEnabled);
  const isThreadInboxActive = isThreadInboxOpen && isWebView;
  const isSmartContextHidden = isSmartContextActive && isThreadInboxActive;
  const isSideDrawerOpen = isSmartContextActive || isThreadInboxActive;

  const bannerText = () => REPLY_BLOCK_BANNER_TEXTS[replyBlockReason] || '';

  if (!activeChannel) {
    return <EmptyView content={texts.noChannelSelected} />;
  }

  if (channelError) {
    return <Error message={chatChannelError[channelError]} />;
  }

  return (
    <>
      <SingleChannelWrapper $sideDrawerVisible={isSideDrawerOpen}>
        <Container title={title} $width={width}>
          <ChannelHeader channel={activeChannel} isWebView={isWebView} />
          {!!voiceNotePilotGroup && (
            <VoiceNotePilotBanner
              data-testid="voice-note-pilot-banner"
              $isVariant={isVoiceNotePilotVariant}
            >
              {isVoiceNotePilotVariant && <VoiceNotePilotMicIcon />}
              {isVoiceNotePilotVariant
                ? texts.voiceNotePilotVariantMessage
                : texts.voiceNotePilotControlMessage}
            </VoiceNotePilotBanner>
          )}
          <ChannelMessageList ref={messageListEndRef} />
          {shouldShowChatInput && (
          <>
            <ChannelMessageInput
              onOpenScheduler={isSchedulerEnabled ? onSchedulerModalOpen : undefined}
              onMessageSent={onMessageSent}
              isMobileView={isMobileView}
              overrideText={overrideText}
              additionalQuery={additionalQuery}
              setSmartContextIsManual={setSmartContextIsManual}
              pendingOutreachType={pendingOutreachType}
            />
            {(pendingScheduledCount > 0 || scheduledCount > 0) && (
            <NotificationBox>
              {pendingScheduledCount > 0 && (
              <TextContainer>
                {format(texts.pendingScheduledCountNotification, {
                  count: pendingScheduledCount,
                  plural: pendingScheduledCount > 1 ? 's' : '',
                })}
                <LinkSpan onClick={() => onClickViewMessagesLink(SCHEDULER_MODAL_TAB.PENDING)}>
                  {texts.seeAllMessages}
                </LinkSpan>
              </TextContainer>
              )}
              {scheduledCount > 0 && (
              <TextContainer>
                {format(texts.scheduledCountNotification, {
                  count: scheduledCount,
                  plural: scheduledCount > 1 ? 's' : '',
                })}
                <LinkSpan onClick={() => onClickViewMessagesLink(SCHEDULER_MODAL_TAB.VIEW)}>
                  {texts.seeAllMessages}
                </LinkSpan>
              </TextContainer>
              )}
            </NotificationBox>
            )}
          </>
          )}
          {shouldShowHiddenBanner && (
            <ClientInactiveNotification>
              {bannerText()}
            </ClientInactiveNotification>
          )}
          {shouldShowHiddenBanner && pendingScheduledCount > 0 && (
            <NotificationBox>
              <TextContainer>
                {format(texts.pendingScheduledCountNotification, {
                  count: pendingScheduledCount,
                  plural: pendingScheduledCount > 1 ? 's' : '',
                })}
                <LinkSpan onClick={() => onClickViewMessagesLink(SCHEDULER_MODAL_TAB.PENDING)}>
                  {texts.seeAllMessages}
                </LinkSpan>
              </TextContainer>
            </NotificationBox>
          )}
          {!isChatFeatureEnabled && !shouldShowHiddenBanner && (
            <ClientInactiveNotification>
              {texts.chatFeatureDisabledMessage}
            </ClientInactiveNotification>
          )}
          <LoadingOverlay
            isLoading={!isChannelReady || isUploading || !isChannelUserDocReady}
            {...(isUploading && {
              spinnerProps: {
                value: totalProgress,
                variant: 'determinate',
                size: 100,
              },
              loadingText: format(texts.uploading, {
                progress: Math.round(totalProgress),
              }),
            })}
          />
        </Container>
        {isThreadInboxActive && <ThreadInbox />}
        {isSmartContextActive && (
          <DrawerSlot $hidden={isSmartContextHidden}>
            <SmartContext
              onInsertClick={(
                text,
                additionalQueryFromSR,
                isManual = false,
                traceId = '',
              ) => {
                setSmartContextText(text);
                setSmartContextIsManual(isManual);
                setAdditionalQuery(additionalQueryFromSR);
                setSmartContextTraceId(traceId);
                logEvent(Event.SMART_CONTEXT_INSERTED, {
                  userId: authUser?.id,
                  timestamp: new Date().toISOString(),
                  channelId: activeChannel?.id,
                  isManual,
                });
              }}
            />
          </DrawerSlot>
        )}
      </SingleChannelWrapper>

      {isSchedulerEnabled && (
        <MessageSchedulerModal
          isOpen={isSchedulerModalOpen}
          currentTab={schedulerModalTab}
          setCurrentTab={setSchedulerModalTab}
          onDidDismiss={onSchedulerModalDismiss}
          showMessageScheduled={showSchedulingSuccessToast}
          deleteOnly={shouldShowHiddenBanner}
        />
      )}
      {!!claimee && (
        <ConfirmDialog
          isOpen={!!claimee}
          onConfirm={reclaimChannel}
          onCancel={() => setClaimee(null)}
          dialogTexts={{
            title: texts.reclaimModalTitle,
            content: format(texts.reclaimModalContent, {
              username: claimee.claimedByUsername,
              time: moment.utc(claimee.claimedAt.toDate()).local().fromNow(),
            }),
            confirm: texts.confirm,
            cancel: texts.ignore,
          }}
        />
      )}
    </>
  );
};

SingleChannelView.propTypes = {
  title: PropTypes.string,
  width: PropTypes.number,
  isWebView: PropTypes.bool,
  isMobileView: PropTypes.bool,
};

SingleChannelView.defaultProps = {
  title: '',
  width: 100,
  isWebView: false,
  isMobileView: false,
};

export default compose(
  withChatChannelContextProviderReady,
  observer,
)(SingleChannelView);
